import type { PaymentReconciliation } from "@medplum/fhirtypes";

import { LEDGER_DETAIL_TYPE } from "@/billing/constants/ledger-detail-type";
import type { StatementPostings } from "@/billing/types/statement-postings";
import { statementNumberOf } from "@/billing/utils/invoice-statement-number";
import { getDetailType } from "@/billing/utils/ledger-detail-type";
import { roundToCents } from "@/billing/utils/money";

/** Whether a reconciliation counts toward a statement's postings: only `active` ones do. */
export function isActivePaymentReconciliation(
  paymentReconciliation: PaymentReconciliation,
): boolean {
  return paymentReconciliation.status === "active";
}

/**
 * The balance left on some charges after their postings: charges − payments + adjustments.
 *
 * Payments are positive and lower the balance. Adjustments are signed as posted: a write-off is
 * negative and lowers the balance, and a correction is positive and raises it. An overpayment
 * gives a negative balance, a credit.
 *
 * @param charges The charges billed.
 * @param postings The payments and adjustments posted against those charges.
 * @returns The balance, rounded to cents.
 */
export function balanceFromPostings(charges: number, postings: StatementPostings): number {
  return roundToCents(charges - postings.payments + postings.adjustments);
}

/**
 * Sums the payments and adjustments posted against each statement.
 *
 * Only active reconciliations count. Each detail line counts by its payment type, so a line that is
 * neither a payment nor an adjustment is skipped.
 *
 * The client Ledger and the Open Statements dashboard both read these sums, so the two screens show
 * the same figures for one statement.
 *
 * @param paymentReconciliations The reconciliations to sum, from any number of statements.
 * @returns The sums keyed by statement number. A statement with nothing posted has no entry.
 */
export function sumPostingsByStatement(
  paymentReconciliations: PaymentReconciliation[],
): Map<string, StatementPostings> {
  const postingsByStatement = new Map<string, StatementPostings>();

  for (const paymentReconciliation of paymentReconciliations) {
    if (!isActivePaymentReconciliation(paymentReconciliation)) continue;

    const statementNumber = statementNumberOf(paymentReconciliation);
    if (!statementNumber) continue;

    const postings = postingsByStatement.get(statementNumber) ?? { payments: 0, adjustments: 0 };
    for (const detail of paymentReconciliation.detail ?? []) {
      const amount = detail.amount?.value ?? 0;
      const type = getDetailType(detail);
      if (type === LEDGER_DETAIL_TYPE.Payment) {
        postings.payments = roundToCents(postings.payments + amount);
      } else if (type === LEDGER_DETAIL_TYPE.Adjustment) {
        postings.adjustments = roundToCents(postings.adjustments + amount);
      }
    }
    postingsByStatement.set(statementNumber, postings);
  }

  return postingsByStatement;
}
